import React, { useEffect, useMemo, useState } from 'react';
import SelectField from '../../Forms/Input/SelectField';
import ButtonCustom from '../../../pages/UiElements/ButtonCustom';
import { useClientProfileDropdown } from '../../../hooks/client/dropdown/useClientProfileDropdown';

/**
 * 📋 ReportProfileSelector
 *
 * Formulario para seleccionar el perfil de cliente
 * cuyo conjunto de reportes se desea configurar.
 */
interface ReportProfileSelectorProps {
  onSearchReports: (profileId: number) => void;
}

const ReportProfileSelector: React.FC<ReportProfileSelectorProps> = ({
  onSearchReports,
}) => {
  const { clientProfileDropdown, fetchClientProfileDropdown } =
    useClientProfileDropdown();
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(
    null
  );

  // 🔄 Cargar opciones al montar
  useEffect(() => {
    fetchClientProfileDropdown();
  }, []);

  // 🧩 Mapeo de opciones
  const mappedOptions = useMemo(
    () =>
      clientProfileDropdown.map((opt) => ({
        value: opt.id,
        label: opt.name,
      })),
    [clientProfileDropdown]
  );

  // 📥 Cambiar selección
  const handleSelectChange = (
    e:
      | React.ChangeEvent<{ name: string; value: number | string }>
      | { target: { name: string; value: number | string } }
  ) => {
    setSelectedProfileId(Number(e.target.value));
  };

  // 🔎 Buscar reportes del perfil
  const handleSearchClick = () => {
    if (selectedProfileId !== null) {
      onSearchReports(selectedProfileId);
    }
  };

  return (
    <div className="w-full">
      {/* 🧭 Encabezado + botón (en la misma línea) */}
      <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-gray-800 dark:text-gray-100 text-base font-semibold">
            Selecciona un perfil de cliente
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            Luego presiona “Buscar Reportes” para ver su configuración.
          </p>
        </div>

        <div className="mt-4 sm:mt-0">
          <ButtonCustom
            text="Buscar Reportes"
            variant="primary"
            onClick={handleSearchClick}
            disabled={selectedProfileId === null}
            iconKey="search"
          />
        </div>
      </div>

      {/* 🧩 Selector de perfil */}
      <div className="w-full sm:w-1/2 md:w-1/3">
        <SelectField
          label="Perfil de Cliente"
          name="profile"
          value={selectedProfileId ?? ''}
          onChange={handleSelectChange}
          options={mappedOptions}
          iconKey="user"
        />
      </div>
    </div>
  );
};

export default ReportProfileSelector;
